import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Table, Select, Tooltip } from 'antd';
import _ from 'lodash';
import styled from 'styled-components';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';

const propTypes = {
  size: PropTypes.string,
  fontSize: PropTypes.string,

  selectable: PropTypes.bool,
  rowSelection: PropTypes.object,
  rowClassName: PropTypes.func,

  color: PropTypes.string,
  title: PropTypes.string,
  icon: PropTypes.string,
  isInsidePanel: PropTypes.bool,

  withColumnFilter: PropTypes.bool,

  // Don't update columns & dataSource props. Use a key prop outside to force component recreation.
  columns: PropTypes.array.isRequired,
  dataSource: PropTypes.array.isRequired,

  tfoot: PropTypes.any, // A react component or a function.
  footer: PropTypes.any, // A react component or a function.
};

const defaultProps = {
  size: 'default', // default, middle, small
  selectable: false,
  fontSize: '14px', // This is the antd table default.

  color: '#000000',
  title: 'table',
  icon: '',
  isInsidePanel: true,

  withColumnFilter: true,
};

const StyledTable = styled(Table)`
  width: 100%;

  tr {
    th, td {
      text-align: center;
      font-size: ${props => props.fontSize};
    }
  }

  tr.highlight, tr.highlight>td {
    background-color: #ffa500 !important;
  }

  tr.danger, tr.danger>td {
    background-color: #f2dede !important;
  }

  tfoot {
    tr>th, tr>td {
      border-bottom: 0;
      border-top: 1px solid #e8e8e8;
      padding: 10px 18px;
      font-weight: 600;
    }

    tr>th:not(:last-child), tr>td:not(:last-child) {
      border-right: 1px solid #e8e8e8;
    }
  }
`;

const exportTableHtmlStyles = `
  @media print { body { -webkit-print-color-adjust: exact; } }
  table {
    border-collapse: collapse;
    text-align: center;
  }

  table, th, td {
    border: 1px solid #909090;
    font-size: 14px;
  }

  table>thead>tr {
    background: #2d4154;
  }

  table>thead>tr>th {
    color: #ffffff;
    padding: 8px 4px;
  }

  table>tbody>tr:nth-child(even) {
    background: #ffffff;
  }

  table>tbody>tr:nth-child(odd) {
    background: #f3f3f3;
  }
`;

// TODO: Real PDF print.
// TODO: multi-column sort:
//  https://github.com/ant-design/ant-design/issues/5057
//  https://github.com/ant-design/ant-design/issues/10946
// TODO: Export should also support multi-column sort.
@autobind
class DataTablesPanel extends BaseComponent {
  constructor(props) {
    super(props);

    this.tableRef = React.createRef();

    this.state = {
      displayColumnTitles: props.columns.map(column => column.title),
      current: 1,
      pageSize: 25,
      searchText: '',
      selectedRowKeys: props.rowSelection?.selectedRowKeys || [],
      sortedInfo: null,
    };
  }

  // Don't update columns & dataSource props. Use a key prop outside to force component recreation.
  componentDidUpdate(prevProps) {
  }

  /* PUBLIC */
  getSelectedRowKeys() {
    return this.state.selectedRowKeys;
  }

  /* PUBLIC */
  setSelectedRowKeys(selectedRowKeys) {
    this.setState({
      selectedRowKeys: selectedRowKeys || [],
    });
  }

  /* PUBLIC */
  getCurrentPage() {
    return this.state.current;
  }

  /* PUBLIC */
  setCurrrentPage(currentPage) {
    this.setState({
      current: currentPage,
    });
  }

  onDisplayColumnSelect(value) {
    if (!this.state.displayColumnTitles.includes(value)) {
      this.setState((prevState, props) => ({
        displayColumnTitles: prevState.displayColumnTitles.concat([value]),
      }));
    }
  }

  onDisplayColumnDeselect(value) {
    if (this.state.displayColumnTitles.includes(value)) {
      this.setState((prevState, props) => ({
        displayColumnTitles: prevState.displayColumnTitles.filter(title => title !== value),
      }));
    }
  }

  onChange(pagination, filters, sorter) {
    this.setState({
      sortedInfo: sorter,
    });
  }

  onPageChange(page, pageSize) {
    if (pageSize) {
      this.setState({
        current: page,
        pageSize,
      });
    } else {
      this.setState({
        current: page,
      });
    }
  }

  onPdfClick(columnsToDisplay, dataRowsToDisplay) {
    this.onPrintClick(columnsToDisplay, dataRowsToDisplay);
  }

  onPrintClick(columnsToDisplay, dataRowsToDisplay) {
    const win = window.open('', 'Export Table', '');
    win.document.body.innerHTML = this.createHtmlData(columnsToDisplay, dataRowsToDisplay);

    // Also create the table styles to be injected.
    const styleTag = win.document.createElement('style');
    win.document.head.appendChild(styleTag);
    styleTag.innerHTML = exportTableHtmlStyles;

    // Open the print dialog.
    win.print();
  }

  async onCsvClick(columnsToDisplay, dataRowsToDisplay) {
    // eslint-disable-next-line
    const tableDom = ReactDOM.findDOMNode(this.tableRef.current);

    // Get TableExport instance.
    const TableExport = await this.importTableExport();
    const exportInstance = new TableExport(tableDom, {
      filename: this.props.title,
      formats: ['csv'],
      exportButtons: false,
    });

    // Save to file.
    const mimeType = 'text/csv';
    const fileName = this.props.title;
    const fileExt = '.csv';
    exportInstance.export2file(
      this.createCsvData(columnsToDisplay, dataRowsToDisplay), mimeType, fileName, fileExt);
  }

  async onExcelClick(columnsToDisplay, dataRowsToDisplay) {
    // eslint-disable-next-line
    const tableDom = ReactDOM.findDOMNode(this.tableRef.current);

    // Get TableExport instance.
    const TableExport = await this.importTableExport();
    const exportInstance = new TableExport(tableDom, {
      filename: this.props.title,
      formats: ['xlsx'],
      exportButtons: false,
    });

    // Save to file.
    const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const fileName = this.props.title;
    const fileExt = '.xlsx';
    exportInstance.export2file(
      this.createExcelData(columnsToDisplay, dataRowsToDisplay), mimeType, fileName, fileExt, undefined, false, 'Sheet1');
  }

  onSelectChange(selectedRowKeys) {
    this.setState({
      selectedRowKeys,
    }, () => {
      if (this.props.rowSelection?.onChange) {
        this.props.rowSelection.onChange(selectedRowKeys);
      }
    });
  }

  // Imports the tableexport library dynamically, because it is huge (>320kB) even
  // after minified and gzipped. https://webpack.js.org/guides/code-splitting/
  async importTableExport() {
    const { default: TableExport } = await import(/* webpackChunkName: "tableexport" */ 'tableexport');
    return TableExport;
  }

  createCsvData(columnsToDisplay, dataRowsToDisplay) {
    const allRows = [];

    // Headers.
    const columnHeaders = [];
    columnsToDisplay.forEach((column) => {
      if (column.dataIndex) {
        columnHeaders.push(`"${column.title}"`);
      }
    });
    allRows.push(columnHeaders.join(','));

    // We need to manually sort the data rows.
    this.sortDataRowsForExport(columnsToDisplay, dataRowsToDisplay);

    // Data rows.
    dataRowsToDisplay.forEach((row) => {
      const rowValues = [];
      columnsToDisplay.forEach((column) => {
        if (column.dataIndex) {
          let value = row[column.dataIndex];

          // For currency values, we need to remove the $ and , signs.
          // We also need to set the type to 'n' to support math operations.
          if (column.dataIndex.endsWith('InDollars') && value?.startsWith('$')) {
            value = value.substring(1).replace(',', '');
          }

          rowValues.push(`"${value || ''}"`);
        }
      });
      allRows.push(rowValues.join(','));
    });

    return allRows.join('\t\n');
  }

  createExcelData(columnsToDisplay, dataRowsToDisplay) {
    const allRows = [];

    // Headers.
    const columnHeaders = [];
    columnsToDisplay.forEach((column) => {
      if (column.dataIndex) {
        columnHeaders.push({ v: `${column.title}`, t: 's' /* Everything string */ });
      }
    });
    allRows.push(columnHeaders);

    // We need to manually sort the data rows.
    this.sortDataRowsForExport(columnsToDisplay, dataRowsToDisplay);

    // Data rows.
    dataRowsToDisplay.forEach((row) => {
      const rowValues = [];
      columnsToDisplay.forEach((column) => {
        if (column.dataIndex) {
          let value = row[column.dataIndex];

          // For currency values, we need to remove the $ and , signs.
          // We also need to set the type to 'n' to support math operations.
          if (column.dataIndex.endsWith('InDollars') && value?.startsWith('$')) {
            value = value.substring(1).replace(',', '');
          }

          rowValues.push({
            v: `${value || ''}`,
            t: column.dataIndex === 'id' ||
               column.dataIndex.endsWith('InDollars') ||
               column.exportAsNumber ? 'n' : 's', /* Everything string */
          });
        }
      });
      allRows.push(rowValues);
    });

    return allRows;
  }

  createHtmlData(columnsToDisplay, dataRowsToDisplay) {
    // Headers.
    const columnHeaders = [];
    columnsToDisplay.forEach((column) => {
      if (column.dataIndex) {
        columnHeaders.push(`<th>${column.title}</th>`);
      }
    });

    // We need to manually sort the data rows.
    this.sortDataRowsForExport(columnsToDisplay, dataRowsToDisplay);

    // Data rows.
    const allRows = [];
    dataRowsToDisplay.forEach((row) => {
      const rowValues = [];
      columnsToDisplay.forEach((column) => {
        if (column.dataIndex) {
          rowValues.push(`<td>${row[column.dataIndex] || ''}</td>`);
        }
      });
      allRows.push(rowValues);
    });

    return `
<h1 style="text-align: center;">${this.props.title}</h1>
<table id="export_table" style="margin-left:auto; margin-right:auto;">
  <thead>
  <tr>
    ${columnHeaders.join('\n')}
  </tr>
  </thead>
  <tbody>
    ${allRows.map(r => `<tr>${r.join('\n')}</tr>`).join('\n')}
  </tbody>
</table>
`;
  }

  sortDataRowsForExport(columnsToDisplay, dataRowsToDisplay) {
    let { sortedInfo } = this.state;

    // If sortedInfo is null, find the defaultSortOrder.
    if (!sortedInfo) {
      const defaultSortColumn = columnsToDisplay.find(c => c.defaultSortOrder);
      if (defaultSortColumn) {
        sortedInfo = {
          columnKey: defaultSortColumn.dataIndex,
          order: defaultSortColumn.defaultSortOrder,
        };
      }
    }

    if (sortedInfo) {
      const sortByColumn = columnsToDisplay.find(c => c.dataIndex === sortedInfo.columnKey);

      if (sortByColumn && sortByColumn.sorter) {
        dataRowsToDisplay.sort(sortByColumn.sorter);
        if (sortedInfo.order === 'descend') {
          dataRowsToDisplay.reverse();
        }
      }
    }
  }

  renderFuncButton(title, onClick) {
    return (
      <styledComp.Span
        width="48px"
        height="32px"
        margin="0 8px 0 0"
        backgroundColor="#e1e5ec"
        backgroundColorHover="#c2cad8"
        border="1 solid #e1e5ec"
        borderHover="1 solid #e1e5ec"
        cursor="pointer"
        textAlign="center"
        color="#666666"
        fontSize="12px"
        fontWeight="600"
        lineHeight="32px"
        onClick={onClick}
      >
        {title}
      </styledComp.Span>
    );
  }

  renderColumnFilter() {
    const { columns } = this.props;
    const { displayColumnTitles } = this.state;

    const allColumnTitles = columns.map(column => column.title);

    return (
      <FlexRowContainer
        width="match_parent"
        height="wrap_content"
        margin="0 0 12px 0"
        alignItems="center"
      >
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          margin="0 8px 0 0"
          color="textPrimaryDark"
          fontSize="16px"
        >
          Showing Columns:
        </styledComp.Span>

        <styledComp.AntdSelect
          width="0"
          height="wrap_content"
          flexGrow="1"
          placeholder="Please select rows to display."
          size="default"
          value={displayColumnTitles}
          mode="multiple"
          optionFilterProp="title"
          defaultValue={allColumnTitles}
          onSelect={this.onDisplayColumnSelect}
          onDeselect={this.onDisplayColumnDeselect}
        >
          {columns.map((column, index) => (
            <Select.Option
              key={column.title}
              value={column.title}
            >
              {column.title}
            </Select.Option>
          ))}
        </styledComp.AntdSelect>
      </FlexRowContainer>
    );
  }

  renderFilterRow() {
    const { pageSize, searchText } = this.state;

    return (
      <FlexRowContainer
        width="match_parent"
        height="wrap_content"
        margin="0 0 12px 0"
        justifyContent="space-between"
        alignItems="center"
      >
        {/* Show 25 Entries */}
        <FlexRowContainer
          width="wrap_content"
          height="wrap_content"
          alignItems="center"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            color="textPrimaryDark"
            fontSize="16px"
          >
            Show
          </styledComp.Span>

          <styledComp.AntdSelect
            width="wrap_content"
            height="wrap_content"
            margin="0 8px"
            placeholder=""
            size="default"
            value={pageSize}
            mode="default"
            optionFilterProp="title"
            onChange={value => this.setState({ pageSize: value })}
          >
            <Select.Option
              key={25}
              value={25}
            >
              25
            </Select.Option>

            <Select.Option
              key={50}
              value={50}
            >
              50
            </Select.Option>

            <Select.Option
              key={75}
              value={75}
            >
              75
            </Select.Option>

            <Select.Option
              key={100}
              value={100}
            >
              100
            </Select.Option>

            <Select.Option
              key={99999}
              value={99999}
            >
              All
            </Select.Option>
          </styledComp.AntdSelect>

          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            color="textPrimaryDark"
            fontSize="16px"
          >
            Entries
          </styledComp.Span>
        </FlexRowContainer>

        <FlexRowContainer
          width="wrap_content"
          height="wrap_content"
          alignItems="center"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            margin="0 8px 0 0"
            color="textPrimaryDark"
            fontSize="16px"
          >
            Search:
          </styledComp.Span>

          <styledComp.AntdInput
            width="200px"
            height="wrap_content"
            color="textPrimaryDark"
            size="small"
            placeholder=""
            value={searchText}
            onChange={e => this.setState({ searchText: e.target.value })}
          />
        </FlexRowContainer>
      </FlexRowContainer>
    );
  }

  renderTableWithTfoot(tfoot, columnsToDisplay, dataRowsToDisplay, key = undefined) {
    // eslint-disable-next-line react/prop-types
    return ({ className, style, children }) => (
      <table className={className} key={key} style={style}>
        {children}
        {_.isFunction(tfoot) ? tfoot(columnsToDisplay, dataRowsToDisplay) : tfoot}
      </table>
    );
  }

  renderContent() {
    const {
      fontSize,
      size,
      selectable,
      rowSelection,
      rowClassName,
      color,
      title,
      icon,
      isInsidePanel,
      withColumnFilter,
      columns,
      dataSource,
      tfoot,
      footer,
      ...layoutProps
    } = this.props;
    const { displayColumnTitles, current, pageSize, searchText, selectedRowKeys } = this.state;

    // Some columns may be filtered.
    const columnsToDisplay = columns.filter(column => displayColumnTitles.includes(column.title));
    const columnsToDisplayDataIndex = columnsToDisplay.map(c => c.dataIndex);

    // Some rows may be filtered.
    const dataRowsToDisplay = dataSource.filter((row) => {
      if (searchText) {
        let matched = false;
        Object.keys(row).some((key) => {
          if (columnsToDisplayDataIndex.includes(key)) {
            const value = row[key];
            const valueAsString = value ? value.toString() : '';
            matched = valueAsString.toLowerCase().includes(searchText.toLowerCase());
            return matched;
          } else {
            return false;
          }
        });
        return matched;
      } else {
        return true;
      }
    });

    // Form the final rowSelection object by combining the one passed in and some preset values.
    let finalRowSelection;
    if (selectable) {
      if (!rowSelection) {
        finalRowSelection = {
          selectedRowKeys,
          onChange: this.onSelectChange,
        };
      } else {
        finalRowSelection = {
          ...rowSelection,
          selectedRowKeys,
          onChange: this.onSelectChange,
        };
      }
    }

    return (
      <FlexColContainer
        {...layoutProps}
        alignItems="center"
        border={isInsidePanel ? `1px solid ${color}` : undefined}
        borderRadius={isInsidePanel ? '2px' : undefined}
      >
        {/* Panel Header */}
        {isInsidePanel &&
          <FlexRowContainer
            width="match_parent"
            height="wrap_content"
            padding="8px 8px"
            justifyContent="space-between"
            alignItems="center"
            backgroundColor={color}
          >
            <FlexRowContainer
              width="wrap_content"
              height="wrap_content"
              alignItems="center"
            >
              {icon &&
                <styledComp.AntdIcon
                  width="wrap_content"
                  height="wrap_content"
                  margin="0 8px 0 0"
                  color="textPrimaryLight"
                  fontSize="14px"
                  type={icon}
                />
              }

              <styledComp.Span
                width="wrap_content"
                height="wrap_content"
                color="textPrimaryLight"
                fontSize="18px"
              >
                {title}
              </styledComp.Span>
            </FlexRowContainer>

            <FlexRowContainer
              width="wrap_content"
              height="wrap_content"
              alignItems="center"
            >
              <Tooltip title="In the shown Print Dialog, set Destination to 'Save as PDF'.">
                {this.renderFuncButton('PDF', () => this.onPdfClick(columnsToDisplay, dataRowsToDisplay))}
              </Tooltip>
              {this.renderFuncButton('CSV', () => this.onCsvClick(columnsToDisplay, dataRowsToDisplay))}
              {this.renderFuncButton('EXCEL', () => this.onExcelClick(columnsToDisplay, dataRowsToDisplay))}
              {this.renderFuncButton('PRINT', () => this.onPrintClick(columnsToDisplay, dataRowsToDisplay))}
            </FlexRowContainer>
          </FlexRowContainer>
        }

        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding={isInsidePanel ? '20px 20px' : '0px 0px'}
        >
          {withColumnFilter && this.renderColumnFilter()}

          {this.renderFilterRow()}

          <StyledTable
            ref={this.tableRef}
            fontSize={fontSize}
            size={size}
            rowSelection={finalRowSelection}
            rowClassName={rowClassName}
            bordered
            pagination={{
              current,
              pageSize,
              showQuickJumper: true,
              showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
              onChange: this.onPageChange,
            }}
            columns={columnsToDisplay}
            dataSource={dataRowsToDisplay}
            components={tfoot
              ? { table: this.renderTableWithTfoot(tfoot, columnsToDisplay, dataRowsToDisplay) }
              : undefined
            }
            onChange={this.onChange}
          />
        </FlexColContainer>

        {!!footer &&
          _.isFunction(footer) ? footer() : footer
        }
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
DataTablesPanel.propTypes = propTypes;
DataTablesPanel.defaultProps = defaultProps;

export default DataTablesPanel;
